/**
 * Status clauses — the state changes the sim actually made (AoD §12.1).
 *
 * This is the pool where the rule "if the log says it, it is true in the state,
 * and vice versa" is either honoured or quietly broken. Every kind here
 * corresponds to exactly one thing the model does: a `WoundEffect` it applied,
 * a `ClockEvent` it emitted. Nothing in here is atmosphere.
 *
 * The blood ladder in particular is worth reading as a set: *pale*, then *grey
 * and slow*, then *down and not getting up*. A reader who learns those three
 * lines has learned the blood clock without ever being shown a number, which is
 * the whole argument for having no HP bar in the first place.
 */
export const STATUSES = {
    /* — what the blow did to the body — */
    sever: [
        '{victim}’s {part} is taken off.',
        'The {part} comes away and {victim} is left with the stump.',
    ],
    break: ['The bone of {victim}’s {part} gives.', '{victim}’s {part} breaks under it.'],
    blind: ['{victim}’s {part} is gone.', 'The {part} is ruined and {victim} will not see with it.'],
    bleeds: ['The wound runs.', 'Blood comes steadily from {victim}’s {part}.'],
    'bleeds-heavily': [
        'The wound bleeds freely.',
        'Blood comes from {victim}’s {part} faster than anyone can staunch it.',
    ],
    /* — what the body did about it (M1-04) — */
    staggered: ['{victim} staggers.', 'The blow drives {victim} back a step.'],
    'knocked-down': ['{victim} goes down.', 'It takes {victim} off {their} feet.'],
    senseless: ['{victim} drops where {victim} stood.', 'The lights go out and {victim} falls.'],
    roused: ['{victim} stirs and comes round.', '{victim} gets an elbow under {them} and lifts.'],
    died: ['{victim} is dead.', '{victim} does not get up again.'],
    'blood-pale': ['{victim} is going pale.', 'The colour is leaving {victim}’s face.'],
    'blood-failing': [
        '{victim} is grey and slow now.',
        '{victim} is losing more blood than {victim} can spare.',
    ],
    'blood-dying': ['{victim} goes down and does not rise.', '{victim} is bled white and sinking.'],
    /* — the slow killer (M1-05) — */
    infected: [
        'The wound in {victim}’s {part} is red and hot.',
        'Something has got into {victim}’s {part}.',
    ],
    'infection-fevered': ['{victim} is flushed and shivering.', 'The fever has {victim} now.'],
    'infection-gangrenous': [
        'The {part} is black at the edges and the smell of it carries.',
        '{victim}’s {part} is dying where it hangs.',
    ],
    'infection-septic': [
        'The rot is in {victim}’s blood and there is no cutting it out.',
        '{victim} burns and there is nothing left to take off.',
    ],
    'infection-terminal': [
        'It takes {victim} in the end.',
        '{victim} does not come out of the fever.',
    ],
    'infection-halted': [
        'The heat goes out of {victim}’s {part}.',
        '{victim}’s {part} is clean again, and mending.',
    ],
    amputated: [
        '{victim}’s {part} comes off, and with it what was in it.',
        'They take {victim}’s {part} and hope it is enough.',
    ],
    /* — leaving — */
    fled: ['{victim} breaks and runs.', '{victim} has had enough and goes.'],
};
//# sourceMappingURL=statuses.js.map